import { useEditor, useNode } from "@craftjs/core";
import * as React from "react";
import { useTranslation } from "react-i18next";
import type { Speaker } from "src/types";
import { useBetween } from "use-between";

import PrimitivesFloatingOptions from "@/components/editor/block-outline/floating-options/primitives-floating-component";
import { SpeakerDetails } from "@/components/editor/shared/speakers/speaker-bio/speaker-details-component";
import {
  ActionText,
  HeadlineField,
  NameField,
  ZoomCard,
} from "@/components/editor/shared/speakers/speaker-bio/speaker-details-styles";
import { SpeakerModal } from "@/components/editor/shared/speakers/speaker-bio/speaker-modal";
import SyncedSpeakersSettings from "@/components/editor/shared/speakers/speakers-settings/synced-speakers-settings-component";
import {
  getSocialLinkAriaLabel,
  useSpeakerModal,
} from "@/components/editor/shared/speakers/utils";
import useTheme from "@/components/editor/themes/use-theme";
import { ColorLabels } from "@/components/editor/utils/use-color";
import { LinkedInSquare } from "@/components/icons/linkedin-square";
import { Twitter } from "@/components/icons/twitter";
import { Website } from "@/components/icons/website";
import useAnalytics from "@/hooks/use-analytics";
import useProperties from "@/hooks/use-properties";
import { isAttendeesPage } from "@/utils/is-attendees-page";

import { Actions, Card, Content, Media } from "./speaker-card-component-styles";

export type SyncedSpeakerCardProps = {
  speaker: Speaker;
  isBioAvailable?: boolean;
};

const SPEAKER_NAME_ID = "speaker-name";
const SPEAKER_HEADLINE_ID = "speaker-headline";

export default function SyncedSpeakerCard({
  speaker,
  isBioAvailable,
}: SyncedSpeakerCardProps) {
  const { enabled } = useEditor((state) => ({
    enabled: state.isEnabled(),
  }));
  const {
    connectors: { connect },
  } = useNode((node) => ({
    linkedNodes: node.getLinkedNodes(),
    nodeId: node.id,
  }));

  const { currentTheme } = useTheme();
  const { speaker: activeSpeaker, setSpeaker } = useBetween(useSpeakerModal);

  const { logAnalytics } = useAnalytics();

  const isActionable = React.useMemo(
    () => isBioAvailable && (isAttendeesPage() || !enabled),
    [enabled, isBioAvailable]
  );

  const handleClick = () => {
    if (isAttendeesPage()) {
      logAnalytics("CANVAS_SPEAKER_CARD_CLICKED", {
        is_bio_enabled: isBioAvailable,
      });
    }

    if (!isAttendeesPage() && enabled) {
      return null;
    }

    setSpeaker(speaker);
  };

  return (
    <>
      <Card data-testid="speaker-card" ref={connect}>
        <Content>
          <ZoomCard
            className={currentTheme.kind}
            actionable={isActionable}
            onClick={handleClick}
          >
            <Media
              data-testid="speaker-profile-picture"
              draggable="false"
              src={speaker.image_url}
              alt={`${speaker.first_name} ${speaker.last_name}`}
            />
          </ZoomCard>
          <div className="mt-2 flex">
            <ActionText actionable={isActionable} onClick={handleClick}>
              <NameField
                data-testid="speaker-name-field"
                id={SPEAKER_NAME_ID}
                className="text-primitive headingFour"
                style={{ fontWeight: "600" }}
              >
                {`${speaker.first_name} ${speaker.last_name}`}
              </NameField>
            </ActionText>
          </div>
          <div className="mb-6 flex">
            <HeadlineField
              id={SPEAKER_HEADLINE_ID}
              data-testid="speaker-headline-field"
            >
              {speaker.headline}
            </HeadlineField>
          </div>
          <SocialLinks speaker={speaker} />
        </Content>
      </Card>
      <SpeakerModal
        isShowing={activeSpeaker?.id === speaker.id}
        onClose={() => setSpeaker(null)}
        isDismissible
        className={currentTheme.kind}
      >
        <SpeakerDetails
          name={`${speaker.first_name} ${speaker.last_name}`}
          headline={speaker.headline}
          bio={speaker.bio}
          social={speaker.social}
          image_url={speaker.image_url}
        />
      </SpeakerModal>
    </>
  );
}

const SocialLinks = ({ speaker }: { speaker: Speaker }) => {
  const { t } = useTranslation();
  const { colors } = useProperties();
  const { social } = speaker;
  const speakerName = `${speaker.first_name} ${speaker.last_name}`;

  if (!social) {
    return null;
  }

  return (
    <Actions>
      {social?.linkedin_url && (
        <a
          href={social.linkedin_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(speakerName, "LinkedIn", t)}
        >
          <LinkedInSquare
            className="size-5"
            style={{ color: colors[ColorLabels.PRIMARY_FONT] }}
            aria-label="LinkedIn"
          />
        </a>
      )}
      {social?.twitter_url && (
        <a
          href={social.twitter_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(speakerName, "Twitter", t)}
        >
          <Twitter
            className="size-5"
            style={{ color: colors[ColorLabels.PRIMARY_FONT] }}
            aria-label="Twitter"
          />
        </a>
      )}
      {social?.website_url && (
        <a
          href={social.website_url}
          target="_blank"
          rel="noreferrer"
          aria-label={getSocialLinkAriaLabel(speakerName, "Website", t)}
        >
          <Website
            className="size-5"
            style={{ color: colors[ColorLabels.PRIMARY_FONT] }}
            aria-label="Website"
          />
        </a>
      )}
    </Actions>
  );
};

SyncedSpeakerCard.craft = {
  custom: {
    displayName: "Synced Speaker Card",
  },
  related: {
    leftOutlineOptions: PrimitivesFloatingOptions,
    settings: SyncedSpeakersSettings,
  },
};
